import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { fetchTools, fetchCategories } from "../lib/api";
import { slugify } from "../lib/utils-tv";

/**
 * Brutalist HTML sitemap. Lists every approved tool grouped by asset
 * class with a flat alphabetical fallback, plus every category /
 * function landing. Boosts internal linking depth (which is the
 * single biggest crawl-budget signal for an index site like this) and
 * gives non-Google search engines a single flat document to discover
 * everything from.
 */
export default function Sitemap() {
  const [tools, setTools] = useState([]);
  const [assets, setAssets] = useState([]);
  const [fns, setFns] = useState([]);

  useEffect(() => {
    fetchTools().then((t) => setTools(t || [])).catch(() => {});
    fetchCategories()
      .then((r) => {
        setAssets(r.asset_classes || []);
        setFns(r.categories || []);
      })
      .catch(() => {});
  }, []);

  const byAsset = useMemo(() => {
    const m = new Map();
    for (const t of tools) {
      const a = t.asset_class || "Other";
      if (!m.has(a)) m.set(a, []);
      m.get(a).push(t);
    }
    for (const [, arr] of m) arr.sort((a, b) => a.title.localeCompare(b.title));
    return m;
  }, [tools]);

  const alphabetical = useMemo(
    () => [...tools].sort((a, b) => a.title.localeCompare(b.title)),
    [tools],
  );

  return (
    <div className="bg-white text-black min-h-screen" data-testid="sitemap-page">
      <Seo
        title="Sitemap"
        path="/sitemap"
        description={`Full index of every tool, category, and asset class on TerminalValue.io — ${tools.length} tools across ${assets.length} asset classes and ${fns.length} functions.`}
      />
      <Header />
      <main className="max-w-5xl mx-auto px-4 py-8">
        <h1
          className="text-xl font-bold border-b border-black pb-2"
          data-testid="sitemap-title"
        >
          &gt;_ sitemap
        </h1>

        <p className="mt-3 text-[12px] text-[#666]">
          {tools.length} tools · {assets.length} asset classes · {fns.length}{" "}
          functions. Plain HTML index for search engines and humans alike.
        </p>

        {/* Section: static pages */}
        <section className="mt-6" data-testid="sitemap-static">
          <h2 className="text-[13px] font-bold text-[#000] border-b border-[#ddd] pb-1">
            pages
          </h2>
          <ul className="list-none p-0 m-0 mt-2 text-[13px] flex flex-wrap gap-x-4 gap-y-1">
            <li>
              <Link to="/" className="tv-link">
                index
              </Link>
            </li>
            <li>
              <Link to="/about" className="tv-link">
                about
              </Link>
            </li>
            <li>
              <Link to="/submit" className="tv-link">
                submit
              </Link>
            </li>
            <li>
              <Link to="/contact" className="tv-link">
                contact
              </Link>
            </li>
            <li>
              <Link to="/terms" className="tv-link">
                terms
              </Link>
            </li>
            <li>
              <Link to="/privacy" className="tv-link">
                privacy
              </Link>
            </li>
            <li>
              <Link to="/tickers" className="tv-link">
                tickers
              </Link>
            </li>
          </ul>
        </section>

        {/* Section: asset class landings */}
        {assets.length > 0 && (
          <section className="mt-6" data-testid="sitemap-assets">
            <h2 className="text-[13px] font-bold text-[#000] border-b border-[#ddd] pb-1">
              by asset class
            </h2>
            <ul className="list-none p-0 m-0 mt-2 text-[13px] flex flex-wrap gap-x-4 gap-y-1">
              {assets.map((a) => (
                <li key={a}>
                  <Link
                    to={`/c/${slugify(a)}`}
                    className="tv-link"
                    data-testid={`sitemap-asset-${slugify(a)}`}
                  >
                    {a}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Section: function landings */}
        {fns.length > 0 && (
          <section className="mt-6" data-testid="sitemap-functions">
            <h2 className="text-[13px] font-bold text-[#000] border-b border-[#ddd] pb-1">
              by function
            </h2>
            <ul className="list-none p-0 m-0 mt-2 text-[13px] flex flex-wrap gap-x-4 gap-y-1">
              {fns.map((f) => (
                <li key={f}>
                  <Link
                    to={`/f/${slugify(f)}`}
                    className="tv-link"
                    data-testid={`sitemap-fn-${slugify(f)}`}
                  >
                    {f}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Section: tools grouped by asset class */}
        <section className="mt-8" data-testid="sitemap-tools-grouped">
          <h2 className="text-[13px] font-bold text-[#000] border-b border-[#ddd] pb-1">
            all tools, grouped
          </h2>
          {[...byAsset.entries()]
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([asset, list]) => (
              <div key={asset} className="mt-4">
                <h3 className="text-[12px] font-bold text-[#000]">
                  <Link
                    to={`/c/${slugify(asset)}`}
                    className="tv-link"
                  >
                    {asset}
                  </Link>{" "}
                  <span className="text-[#888] font-normal">
                    ({list.length})
                  </span>
                </h3>
                <ul className="list-none p-0 m-0 mt-1 text-[13px] flex flex-wrap gap-x-3 gap-y-1">
                  {list.map((t) => (
                    <li key={t.id}>
                      <Link
                        to={`/tool/${t.slug}`}
                        className="tv-link"
                        title={t.description}
                        data-testid={`sitemap-tool-${t.slug}`}
                      >
                        {t.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
        </section>

        {/* Section: A-Z fallback */}
        <section className="mt-8 mb-12" data-testid="sitemap-tools-alpha">
          <h2 className="text-[13px] font-bold text-[#000] border-b border-[#ddd] pb-1">
            A → Z
          </h2>
          <ul className="list-none p-0 m-0 mt-2 text-[13px] flex flex-wrap gap-x-3 gap-y-1">
            {alphabetical.map((t) => (
              <li key={t.id}>
                <Link
                  to={`/tool/${t.slug}`}
                  className="tv-link"
                  title={t.description}
                  data-testid={`sitemap-alpha-${t.slug}`}
                >
                  {t.title}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      </main>
      <Footer />
    </div>
  );
}
